import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import SalarySubNav from './SalarySubNav';
import { apiGet } from './api';
import { fmtCurrency, fmtDate } from './formatters';
import type { T4Summary as T4Row } from './types';
import DataTable from '../../shared/components/DataTable';

const T4Summary: React.FC = () => {
  const { sessionToken } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState<T4Row[]>([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(''); setRows([]);
    (async () => {
      try {
        const res = await apiGet<{ employees: T4Row[] }>(`/t4/${year}`, sessionToken);
        if (!cancelled) setRows(res.employees);
      } catch (e: any) { if (!cancelled) setErr(e.message); } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [year, sessionToken]);

  const totals = rows.reduce((a, r) => ({
    gross: a.gross + Number(r.gross),
    tax: a.tax + Number(r.tax),
    cpp: a.cpp + Number(r.cpp_employee),
    ei: a.ei + Number(r.ei_employee),
    net: a.net + Number(r.net),
  }), { gross: 0, tax: 0, cpp: 0, ei: 0, net: 0 });

  return (
    <div className="salary-module">
      <h2>T4 Annual Summary</h2>
      <SalarySubNav />
      {err && <div className="error-banner">{err}</div>}

      <div className="salary-actions">
        <label htmlFor="t4-year" style={{ marginRight: '0.5rem' }}>Year:</label>
        <input
          id="t4-year" inputMode="numeric" min="2000" max="2100" type="number"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          style={{ width: 100, padding: '0.4rem', borderRadius: 5, border: '1px solid rgba(33,8,19,0.2)' }}
        />
        <span style={{ color: '#666', fontSize: '0.9rem' }}>
          T4 slips are due to CRA and employees by February 28 of the following year.
          Review missing profile details before preparing slips. Authorized users can access encrypted SINs through User Profiles.
        </span>
      </div>

      {loading ? <p role="status">Loading {year} summary…</p> : !err && <div className="salary-card"><h3>{year} at a glance</h3><p>{rows.length} employees · Gross {fmtCurrency(totals.gross)} · Income tax {fmtCurrency(totals.tax)}</p><p>{rows.filter(row => !row.address || !row.sin_last3).length} profiles need address or SIN details reviewed.</p></div>}
      <div className="salary-card">
        <DataTable
          rows={rows}
          rowKey={(r) => r.employee_id}
          caption={`T4 summary ${year}`}
          moreLabel="Deductions & details"
          empty={`No entries for ${year}.`}
          columns={[
            { key: 'employee', header: 'Employee', priority: 'primary', render: (r) => r.employee_name },
            { key: 'gross', header: 'Gross', priority: 'summary', numeric: true, render: (r) => fmtCurrency(r.gross) },
            { key: 'tax', header: 'Tax', priority: 'summary', numeric: true, render: (r) => fmtCurrency(r.tax) },
            { key: 'net', header: 'Net', priority: 'summary', numeric: true, render: (r) => <strong>{fmtCurrency(r.net)}</strong> },
            { key: 'cpp', header: 'CPP', priority: 'detail', numeric: true, render: (r) => fmtCurrency(r.cpp_employee) },
            { key: 'ei', header: 'EI', priority: 'detail', numeric: true, render: (r) => fmtCurrency(r.ei_employee) },
            { key: 'sin', header: 'SIN (last 3)', priority: 'detail', render: (r) => (r.sin_last3 ? `XXX-XX-*${r.sin_last3}` : '—') },
            { key: 'address', header: 'Address', priority: 'detail', render: (r) => r.address || '—' },
            { key: 'hire', header: 'Hire Date', priority: 'detail', render: (r) => fmtDate(r.hire_date) },
          ]}
          footer={
            <tr>
              <td><strong>Totals</strong></td>
              <td className="num"><strong>{fmtCurrency(totals.gross)}</strong></td>
              <td className="num"><strong>{fmtCurrency(totals.tax)}</strong></td>
              <td className="num"><strong>{fmtCurrency(totals.net)}</strong></td>
              <td className="num"><strong>{fmtCurrency(totals.cpp)}</strong></td>
              <td className="num"><strong>{fmtCurrency(totals.ei)}</strong></td>
              <td colSpan={3} />
            </tr>
          }
          footerCard={
            <>
              <div className="cs-record__title">Totals {year}</div>
              <dl className="cs-record__fields">
                <div className="cs-record__field"><dt>Gross</dt><dd className="num">{fmtCurrency(totals.gross)}</dd></div>
                <div className="cs-record__field"><dt>Tax</dt><dd className="num">{fmtCurrency(totals.tax)}</dd></div>
                <div className="cs-record__field"><dt>CPP</dt><dd className="num">{fmtCurrency(totals.cpp)}</dd></div>
                <div className="cs-record__field"><dt>EI</dt><dd className="num">{fmtCurrency(totals.ei)}</dd></div>
                <div className="cs-record__field"><dt>Net</dt><dd className="num">{fmtCurrency(totals.net)}</dd></div>
              </dl>
            </>
          }
        />
      </div>
    </div>
  );
};

export default T4Summary;
